import clsx from 'clsx';
import { Label } from '../Label';
import { CustomSwitch, SwitchProps } from './CustomSwitch';
import { ChangeEvent, ReactNode } from 'react';

interface Props extends BaseProps {
  checked?: boolean;
  color?: ColorVariant;
  checkedIcon?: ReactNode;
  direction?: FlexDirection;
  disabled?: boolean;
  icon?: ReactNode;
  label?: string;
  labelOpacity?: 'full' | 'default';
  name?: string;
  onChange?(event: ChangeEvent<HTMLInputElement>): any;
  position?: FlexPosition;
  required?: boolean;
  size?: SizeVariant;
  variant?: 'contained' | 'overflow';
}

export const Switch = (props: Props) => {
  const {
    checked = false,
    checkedIcon,
    className,
    color = 'primary',
    direction = 'column',
    disabled = false,
    icon,
    label = null,
    labelOpacity = 'full',
    onChange,
    position = 'start',
    required = false,
    size = 'medium',
    variant = 'contained',
    ...rest
  } = props;
  const formattedLabel = (label ?? 'default')
    .toLowerCase()
    .trim()
    .replaceAll(' ', '-');
  const classes = useStyles();

  const switchProps: SwitchProps = {
    checked: checked,
    checkedIcon: checkedIcon,
    color: color,
    disabled: disabled,
    icon: icon,
    size: size,
    variant: variant,
  };

  const renderSwitch = () => {
    if (label)
      return (
        <Label
          id={`label-${formattedLabel}`}
          aria-label={label}
          aria-disabled={disabled}
          classNames={{ label: '!text-xs opacity-70' }}
          direction={direction}
          align="center"
          required={required}
          text={label}
          opacity={labelOpacity}
          position={position}
        >
          <CustomSwitch {...switchProps} />
        </Label>
      );
    return <CustomSwitch {...switchProps} />;
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (onChange !== undefined) onChange(event);
  };

  return (
    <div className={clsx(classes.root, { [classes.disabled]: disabled })}>
      {renderSwitch()}
      <input
        id={`${formattedLabel}-switch`}
        aria-labelledby={`label-${formattedLabel}`}
        aria-required={required}
        aria-disabled={disabled}
        className={classes.input.default}
        checked={checked}
        onChange={handleChange}
        required={required}
        tabIndex={0}
        type="checkbox"
        {...rest}
      />
    </div>
  );
};

const useStyles = () => ({
  root: 'flex !items-center m-1 flex-row relative',
  disabled: 'opacity-30 dark:opacity-50 pointer-events-none cursor-default',
  input: {
    default:
      'absolute cursor-pointer appearance-none w-full h-full opacity-0 cursor-pointer',
  },
});
